import { Box, Text } from "@saleor/macaw-ui-next";
import { defineMessages, type IntlShape, useIntl } from "react-intl";

// The pieces the request-log views share: the API keys usage card and the
// store-wide API usage page.

const usageFormatMessages = defineMessages({
  latencyValue: {
    id: "7Vmv6r",
    defaultMessage: "{ms} ms",
    description: "request log usage, a latency in milliseconds",
  },
});

/** A mean latency in whole milliseconds, or a dash for no requests. */
export const formatLatency = (intl: IntlShape, ms: number | null) =>
  ms === null
    ? "-"
    : intl.formatMessage(usageFormatMessages.latencyValue, {
        ms: intl.formatNumber(Math.round(ms)),
      });

/** A Saleor query cost, to one decimal. */
export const formatCost = (intl: IntlShape, cost: number) =>
  intl.formatNumber(cost, { maximumFractionDigits: 1 });

/** One headline number with its label. */
export const UsageTotal = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}) => (
  <Box display="flex" flexDirection="column" gap={1}>
    <Text size={2} color="default2">
      {label}
    </Text>
    <Text size={5} fontWeight="medium" data-test-id={testId}>
      {value}
    </Text>
  </Box>
);

const BAR_HEIGHT_PX = 48;

/**
 * Requests per day as a strip of bars, oldest on the left. Plain boxes in the
 * theme's accent: the trend is all it has to show, and the tables beside it
 * hold the numbers.
 */
export const RequestStrip = ({
  rows,
  testId,
}: {
  rows: Array<{ day: string; requests: number }>;
  testId: string;
}) => {
  const intl = useIntl();
  const peak = Math.max(1, ...rows.map(row => row.requests));

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      gap={0.5}
      __height={`${BAR_HEIGHT_PX}px`}
      data-test-id={testId}
    >
      {rows.map(row => (
        <Box
          key={row.day}
          __flex="1 1 0"
          backgroundColor={row.requests ? "accent1" : "default2"}
          borderRadius={1}
          __height={`${Math.max(2, Math.round((row.requests / peak) * BAR_HEIGHT_PX))}px`}
          title={`${row.day}: ${intl.formatNumber(row.requests)}`}
        />
      ))}
    </Box>
  );
};
